import React, { useState, useEffect } from 'react';
import { getGlobalSettings, updateGlobalSettings } from '../services/settingsService';

export default function HeaderManagement() {
  const [headerScripts, setHeaderScripts] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await getGlobalSettings();
      setHeaderScripts(settings.headerScripts || '');
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    const success = await updateGlobalSettings({ headerScripts });
    
    if (success) {
      setMessage({ type: 'success', text: 'Header scripts updated successfully!' });
    } else {
      setMessage({ type: 'error', text: 'Failed to update header scripts. Please try again.' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Header Management</h1>
        <p className="text-slate-400 text-lg">Add scripts or meta tags to the website header.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                Header Scripts (HTML/JS)
              </label>
              <span className="text-xs text-slate-500 italic">Injects directly into &lt;head&gt;</span>
            </div>
            
            <textarea
              className="w-full h-64 px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all placeholder:text-slate-700"
              placeholder="<!-- Paste your Google AdSense or Analytics script here -->"
              value={headerScripts}
              onChange={(e) => setHeaderScripts(e.target.value)}
            />
            
            <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/20 text-blue-300/90 text-xs leading-relaxed">
              <p className="font-bold mb-1 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Tip
              </p>
              You can add Google Analytics, AdSense, Meta Tags, or any other integration scripts here. They will be placed before the closing &lt;/head&gt; tag on your public website.
            </div>
          </div>

          {message.text && (
            <div className={`p-4 rounded-xl text-sm font-medium border ${
              message.type === 'success' 
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                : 'bg-red-500/10 text-red-400 border-red-500/20'
            }`}>
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className={`w-full py-4 px-6 rounded-xl font-bold text-white transition-all shadow-lg hover:shadow-blue-500/20 active:scale-[0.98] ${
              saving 
                ? 'bg-slate-700 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500'
            }`}
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving Changes...
              </span>
            ) : 'Update Header Scripts'}
          </button>
        </div>
      </form>
    </div>
  );
}
